/**
 * RTWO Smart Contract Runtime — Domain Models
 * Phase 13 | Module ID: RTWO-SCRT-001
 * Version: 1.0.0-phase13
 *
 * Strongly typed domain models for the RTWO Smart Contract Runtime.
 * Defines the programmable execution layer for the RTWO blockchain ecosystem.
 *
 * Truthful Data Policy:
 * - No fake contracts, addresses, deployments, executions, ABIs, gas usage, or contract events.
 * - Smart contracts are NOT deployed — architecture phase only.
 * - Runtime execution is NOT operational.
 * - EVM compatibility is NOT claimed.
 * - All runtime values are null until real Runtime Backend and Ledger Connection are established.
 *
 * Source: Smart Contract Runtime (RTWO-SCRT-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type ContractId = string;
export type DeploymentId = string;
export type ExecutionId = string;
export type ContractStateId = string;
export type ContractEventId = string;
export type ContractABIId = string;
export type ContractPermissionId = string;
export type RuntimePolicyId = string;
export type SCRTTimestamp = number | null;

export const SCRT_ENGINE_VERSION = {
  moduleId: 'RTWO-SCRT-001',
  moduleName: 'Smart Contract Runtime',
  version: '1.0.0-phase13',
  phase: 'Phase 13 — Smart Contract Runtime',
  status: 'Structure Complete — Waiting for Real Contract Data',
  productionReadiness: 'Pending Runtime Backend and Ledger Connection',
  policy:
    'Truthful Data Policy Enforced — No Fake Contracts, Addresses, Deployments, Executions, ABIs, Gas Usage, or Contract Events',
} as const;

// ─────────────────────────────────────────────
// CONTRACT TYPES
// ─────────────────────────────────────────────

export type RTWOContractType =
  | 'native_rtwo'
  | 'asset_contract'
  | 'governance_contract'
  | 'staking_contract'
  | 'bridge_contract'
  | 'utility_contract'
  | 'system_contract'
  | 'custom';

export type RTWOContractStatus =
  | 'architecture_ready'
  | 'not_deployed'
  | 'waiting_for_runtime_backend'
  | 'waiting_for_ledger_connection'
  | 'deployed'
  | 'paused'
  | 'deprecated'
  | 'failed';

export const CONTRACT_STATUS_LABELS: Record<RTWOContractStatus, string> = {
  architecture_ready: 'Architecture Ready',
  not_deployed: 'Not Deployed',
  waiting_for_runtime_backend: 'Waiting for Runtime Backend',
  waiting_for_ledger_connection: 'Waiting for Ledger Connection',
  deployed: 'Deployed',
  paused: 'Paused',
  deprecated: 'Deprecated',
  failed: 'Failed',
};

// ─────────────────────────────────────────────
// DEPLOYMENT LIFECYCLE
// ─────────────────────────────────────────────

export type RTWODeploymentStatus =
  'created' | 'validated' | 'queued' | 'deploying' | 'deployed' | 'failed' | 'rejected';

export interface DeploymentLifecycleStage {
  status: RTWODeploymentStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealData: boolean;
  truthfulNote: string;
}

export const DEPLOYMENT_LIFECYCLE: DeploymentLifecycleStage[] = [
  {
    status: 'created',
    label: 'Created',
    description:
      'Deployment record created. Bytecode reference and source reference recorded. Deployer address reference registered.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Runtime Backend. No fake deployments created.',
  },
  {
    status: 'validated',
    label: 'Validated',
    description:
      'Bytecode and ABI validated against RTWO runtime policy. Permissions checked. Gas policy evaluated.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Runtime Validation Service. No fake validation results.',
  },
  {
    status: 'queued',
    label: 'Queued',
    description:
      'Deployment queued for execution. Waiting for runtime worker to process the deployment transaction.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real runtime worker infrastructure. No fake queue depth shown.',
  },
  {
    status: 'deploying',
    label: 'Deploying',
    description:
      'Runtime is executing the deployment transaction. Contract address being assigned by the Ledger Engine.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Ledger Engine connection. No fake contract addresses generated.',
  },
  {
    status: 'deployed',
    label: 'Deployed',
    description:
      'Contract successfully deployed. Contract address confirmed by Ledger Engine. ABI registered.',
    terminal: true,
    requiresRealData: true,
    truthfulNote:
      'Terminal success state. Requires real Ledger Engine confirmation. No fake deployed contracts.',
  },
  {
    status: 'failed',
    label: 'Failed',
    description:
      'Deployment failed at any lifecycle stage. Error details recorded. Deployment may be retried after correction.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal failure state. Error details must come from real backend.',
  },
  {
    status: 'rejected',
    label: 'Rejected',
    description:
      'Deployment rejected by runtime policy, permission service, or validation service. Cannot be retried without correction.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal rejection state. Rejection reason must come from real policy engine.',
  },
];

export const DEPLOYMENT_STATUS_LABELS: Record<RTWODeploymentStatus, string> = {
  created: 'Created',
  validated: 'Validated',
  queued: 'Queued',
  deploying: 'Deploying',
  deployed: 'Deployed',
  failed: 'Failed',
  rejected: 'Rejected',
};

// ─────────────────────────────────────────────
// EXECUTION LIFECYCLE
// ─────────────────────────────────────────────

export type RTWOExecutionStatus =
  'requested' | 'validated' | 'executing' | 'completed' | 'reverted' | 'failed';

export interface ExecutionLifecycleStage {
  status: RTWOExecutionStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealData: boolean;
  truthfulNote: string;
}

export const EXECUTION_LIFECYCLE: ExecutionLifecycleStage[] = [
  {
    status: 'requested',
    label: 'Requested',
    description:
      'Execution request received. Caller address reference, method name, and input reference recorded.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Runtime Backend. No fake executions created.',
  },
  {
    status: 'validated',
    label: 'Validated',
    description:
      'Execution request validated. Caller permissions checked. Method signature verified against ABI. Gas policy evaluated.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real ABI Registry and Permission Service. No fake validation.',
  },
  {
    status: 'executing',
    label: 'Executing',
    description:
      'Runtime is executing the contract method. State changes being applied to the contract state.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real Runtime Engine. No fake execution results.',
  },
  {
    status: 'completed',
    label: 'Completed',
    description:
      'Execution completed successfully. Output reference recorded. State changes committed to Ledger Engine.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal success state. Requires real Ledger Engine commit. No fake outputs.',
  },
  {
    status: 'reverted',
    label: 'Reverted',
    description:
      'Execution reverted by contract logic. State changes rolled back. Revert reason recorded.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal revert state. Revert reason must come from real runtime.',
  },
  {
    status: 'failed',
    label: 'Failed',
    description:
      'Execution failed due to runtime error. State changes rolled back. Error details recorded.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal failure state. Error details must come from real backend.',
  },
];

export const EXECUTION_STATUS_LABELS: Record<RTWOExecutionStatus, string> = {
  requested: 'Requested',
  validated: 'Validated',
  executing: 'Executing',
  completed: 'Completed',
  reverted: 'Reverted',
  failed: 'Failed',
};

// ─────────────────────────────────────────────
// RUNTIME TYPES
// ─────────────────────────────────────────────

export type RTWORuntimeType = 'rtwo_native' | 'wasm' | 'custom_vm' | 'not_configured';

export type RTWOContractStateStatus =
  | 'architecture_ready'
  | 'not_initialized'
  | 'waiting_for_runtime_backend'
  | 'active'
  | 'stale'
  | 'corrupted';

export type RTWOContractEventStatus =
  'architecture_ready' | 'not_emitted' | 'waiting_for_event_bus' | 'emitted' | 'failed';

export type RTWOABIStatus = 'architecture_ready' | 'not_registered' | 'registered' | 'deprecated';

export type RTWOPermissionType =
  'deploy' | 'execute' | 'read_state' | 'upgrade' | 'pause' | 'admin';

export type RTWOPermissionStatus =
  'architecture_ready' | 'not_configured' | 'granted' | 'denied' | 'revoked';

export type RTWORuntimePolicyStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOSmartContract
// ─────────────────────────────────────────────

export interface RTWOSmartContract {
  /** Unique identifier for this smart contract */
  contractId: ContractId;
  /** Contract address — reference only, not generated. Null until deployed. */
  contractAddress: string | null;
  /** Human-readable contract name */
  contractName: string;
  /** Type classification of this contract */
  contractType: RTWOContractType;
  /** Reference to the owner address — not a real address, reference only */
  ownerAddressReference: string | null;
  /** Runtime version this contract targets */
  runtimeVersion: string | null;
  /** Current contract status */
  status: RTWOContractStatus;
  /** Timestamp when the contract record was created */
  createdAt: SCRTTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOContractDeployment
// ─────────────────────────────────────────────

export interface RTWOContractDeployment {
  /** Unique identifier for this deployment record */
  deploymentId: DeploymentId;
  /** Reference to the contract being deployed */
  contractId: ContractId;
  /** Reference to the deployer address — not a real address, reference only */
  deployerAddressReference: string | null;
  /** Reference to the bytecode — not actual bytecode, reference only */
  bytecodeReference: string | null;
  /** Reference to the source code — not actual source, reference only */
  sourceReference: string | null;
  /** Current deployment lifecycle status */
  deploymentStatus: RTWODeploymentStatus;
  /** Timestamp when the deployment record was created */
  createdAt: SCRTTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOContractExecution
// ─────────────────────────────────────────────

export interface RTWOContractExecution {
  /** Unique identifier for this execution record */
  executionId: ExecutionId;
  /** Reference to the contract being executed */
  contractId: ContractId;
  /** Reference to the caller address — not a real address, reference only */
  callerAddressReference: string | null;
  /** Name of the method being called */
  methodName: string | null;
  /** Reference to the input data — not actual input, reference only */
  inputReference: string | null;
  /** Reference to the output data — not actual output, reference only */
  outputReference: string | null;
  /** Current execution lifecycle status */
  executionStatus: RTWOExecutionStatus;
  /** Timestamp when the execution was requested */
  createdAt: SCRTTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOContractState
// ─────────────────────────────────────────────

export interface RTWOContractState {
  /** Unique identifier for this contract state record */
  stateId: ContractStateId;
  /** Reference to the contract whose state this represents */
  contractId: ContractId;
  /** State root hash — reference only, not generated */
  stateRoot: string | null;
  /** Reference to the storage backend — not actual storage, reference only */
  storageReference: string | null;
  /** Current state status */
  status: RTWOContractStateStatus;
  /** Timestamp when the state was last updated */
  updatedAt: SCRTTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOContractEvent
// ─────────────────────────────────────────────

export type RTWOContractEventType =
  | 'transfer'
  | 'approval'
  | 'deployment'
  | 'upgrade'
  | 'pause'
  | 'unpause'
  | 'ownership_transfer'
  | 'custom';

export interface RTWOContractEvent {
  /** Unique identifier for this contract event record */
  eventId: ContractEventId;
  /** Reference to the contract that emitted this event */
  contractId: ContractId;
  /** Type of event emitted */
  eventType: RTWOContractEventType;
  /** Reference to the event payload — not actual payload, reference only */
  payloadReference: string | null;
  /** Reference to the block containing this event — not a real block, reference only */
  blockReference: string | null;
  /** Reference to the transaction containing this event — not a real tx, reference only */
  transactionReference: string | null;
  /** Current event status */
  status: RTWOContractEventStatus;
  /** Timestamp when the event was emitted */
  emittedAt: SCRTTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOContractABI
// ─────────────────────────────────────────────

export interface RTWOContractABI {
  /** Unique identifier for this ABI record */
  abiId: ContractABIId;
  /** Reference to the contract this ABI belongs to */
  contractId: ContractId;
  /** Reference to the ABI definition — not actual ABI JSON, reference only */
  abiReference: string | null;
  /** ABI version string */
  version: string | null;
  /** Current ABI status */
  status: RTWOABIStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWOContractPermission
// ─────────────────────────────────────────────

export interface RTWOContractPermission {
  /** Unique identifier for this permission record */
  permissionId: ContractPermissionId;
  /** Reference to the contract this permission applies to */
  contractId: ContractId;
  /** Type of permission */
  permissionType: RTWOPermissionType;
  /** Reference to the subject (address, role, or module) — reference only */
  subjectReference: string | null;
  /** Current permission status */
  status: RTWOPermissionStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOContractRuntimePolicy
// ─────────────────────────────────────────────

export interface RTWOGasPolicy {
  gasPolicyId: string;
  maxGasPerExecution: number | null;
  gasPriceReference: string | null;
  status: 'not_configured' | 'active';
}

export interface RTWOPermissionPolicy {
  permissionPolicyId: string;
  defaultPermission: 'allow' | 'deny' | 'not_configured';
  requiresExplicitGrant: boolean;
  status: 'not_configured' | 'active';
}

export interface RTWOUpgradePolicy {
  upgradePolicyId: string;
  upgradeAllowed: boolean;
  upgradeRequiresGovernance: boolean;
  status: 'not_configured' | 'active';
}

export interface RTWOContractRuntimePolicy {
  /** Unique identifier for this runtime policy */
  policyId: RuntimePolicyId;
  /** Runtime type this policy applies to */
  runtimeType: RTWORuntimeType;
  /** Gas policy configuration */
  gasPolicy: RTWOGasPolicy;
  /** Permission policy configuration */
  permissionPolicy: RTWOPermissionPolicy;
  /** Upgrade policy configuration */
  upgradePolicy: RTWOUpgradePolicy;
  /** Current policy status */
  status: RTWORuntimePolicyStatus;
}

// ─────────────────────────────────────────────
// SERVICE IDS
// ─────────────────────────────────────────────

export const SCRT_SERVICE_IDS = {
  CONTRACT_REGISTRY: 'scrt-svc-001',
  DEPLOYMENT_LIFECYCLE: 'scrt-svc-002',
  EXECUTION_LIFECYCLE: 'scrt-svc-003',
  RUNTIME_POLICY: 'scrt-svc-004',
  CONTRACT_STATE: 'scrt-svc-005',
  EVENT_PUBLISHER: 'scrt-svc-006',
  ABI_REGISTRY: 'scrt-svc-007',
  PERMISSION_SERVICE: 'scrt-svc-008',
  RUNTIME_VALIDATION: 'scrt-svc-009',
  RUNTIME_READINESS: 'scrt-svc-010',
} as const;

// ─────────────────────────────────────────────
// ENGINE STATUS
// ─────────────────────────────────────────────

export type RTWOSCRTEngineStatus =
  | 'not_initialized'
  | 'initializing'
  | 'structure_ready'
  | 'waiting_for_runtime_backend'
  | 'waiting_for_ledger_connection'
  | 'operational'
  | 'error'
  | 'shutdown';

// ─────────────────────────────────────────────
// SCRT EVENT TYPES
// ─────────────────────────────────────────────

export type SCRTEventType =
  | 'contract_registered'
  | 'deployment_created'
  | 'deployment_status_changed'
  | 'execution_requested'
  | 'execution_status_changed'
  | 'abi_registered'
  | 'permission_granted'
  | 'policy_configured'
  | 'state_updated';

export interface SCRTEvent {
  eventId: string;
  eventType: SCRTEventType;
  moduleId: 'RTWO-SCRT-001';
  timestamp: SCRTTimestamp;
  payload: Record<string, unknown>;
}

export type SCRTEventHandler = (event: SCRTEvent) => void;
